import React, { useMemo, useState } from "react";
import { Card, Col, Row, Table, Tag, Typography } from "antd";
import { useGetUsersQuery } from "../../api/authApi";

const { Title } = Typography;

const formatDate = (value) => {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const statusColor = (status) => {
  switch (status) {
    case "ACTIVE":
      return "green";
    case "APPROVAL_PENDING":
    case "PENDING":
      return "orange";
    case "PAUSED":
      return "gold";
    case "CANCELED":
      return "red";
    default:
      return "default";
  }
};

const SubscriptionsComponent = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const { data, isLoading } = useGetUsersQuery({
    page,
    limit,
    sortBy: "firstName",
    sortOrder: "ASC",
  });

  const users = data?.users || [];

  const subscriptions = useMemo(
    () =>
      users
        .filter((u) => u?.subscription)
        .map((u) => ({
          key: u.id,
          userName: `${u.firstName || ""} ${u.lastName || ""}`.trim() || "N/A",
          email: u.email || "N/A",
          userStatus: u.status || "-",
          planType: u.subscription?.planType || "-",
          billingCycle: u.subscription?.plan || "-",
          status: u.subscription?.status || "-",
          price: u.subscription?.price != null ? `$${u.subscription.price}` : "-",
          createdAt: u.subscription?.createdAt || u.subscription?.created || null,
          updatedAt: u.subscription?.updatedAt || null,
        })),
    [users]
  );

  const recentThreshold = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  }, []);

  const newSubscriptions = useMemo(
    () =>
      subscriptions.filter((s) => {
        if (!s.createdAt) return false;
        const dt = new Date(s.createdAt);
        return !Number.isNaN(dt.getTime()) && dt >= recentThreshold;
      }),
    [subscriptions, recentThreshold]
  );

  const listingColumns = [
    { title: "User", dataIndex: "userName", key: "userName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Plan Type", dataIndex: "planType", key: "planType" },
    { title: "Billing", dataIndex: "billingCycle", key: "billingCycle" },
    {
      title: "Subscription Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    { title: "Price", dataIndex: "price", key: "price" },
  ];

  const historyColumns = [
    { title: "User", dataIndex: "userName", key: "userName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "User Status", dataIndex: "userStatus", key: "userStatus" },
    {
      title: "Subscription Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusColor(status)}>{status}</Tag>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => formatDate(v),
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (v) => formatDate(v),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>
        Subscriptions
      </Title>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card>
            <div>Total Subscriptions</div>
            <Title level={3} style={{ margin: 0 }}>
              {subscriptions.length}
            </Title>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <div>New Subscriptions (7 days)</div>
            <Title level={3} style={{ margin: 0 }}>
              {newSubscriptions.length}
            </Title>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <div>Active Subscriptions</div>
            <Title level={3} style={{ margin: 0 }}>
              {subscriptions.filter((s) => s.status === "ACTIVE").length}
            </Title>
          </Card>
        </Col>
      </Row>

      <Card title="New Subscriptions" style={{ marginBottom: 16 }}>
        <Table
          columns={listingColumns}
          dataSource={newSubscriptions}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      <Card title="Subscription Listings" style={{ marginBottom: 16 }}>
        <Table
          columns={listingColumns}
          dataSource={subscriptions}
          loading={isLoading}
          pagination={false}
          scroll={{ x: 900 }}
        />
      </Card>

      <Card title="User Subscription History">
        <Table
          columns={historyColumns}
          dataSource={subscriptions}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: limit,
            total: data?.total || 0,
            showSizeChanger: false,
            onChange: (nextPage, nextLimit) => {
              setPage(nextPage);
              setLimit(nextLimit);
            },
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default SubscriptionsComponent;
